import React, { useState, useEffect, Fragment, useCallback } from 'react';
import get from 'lodash.get';

import ordersService from '../../data/services/orders.service';
import prescriptionRequestService from '../../data/services/prescriptionRequest.service';
import addressesService from '../../data/services/addresses.service';
import useDataApi from '../../hooks/useDataApi';
import useDataApiEffect from '../../hooks/useDataApiEffect';
import { API_ERROR_MESSAGE } from '../../constants';
import validationSchema from './schemas/validationSchema';

import ButtonBusy from '../_ui/_blocks/Buttons/ButtonBusy/ButtonBusy';
import Loading from '../_ui/_blocks/Loading/Loading';
import AddressForm from './AddressForm/AddressForm';
import AddressPicker from './AddressPicker';
import { JoiForm } from '../_ui/_blocks/Form/JoiForm/JoiForm';
import JoiFormInputWrap from '../_ui/_blocks/Form/JoiForm/JoiFormInputWrap/JoiFormInputWrap';
import SelectPersonalTitle, {
    titleSlugToReadable,
    titleReadableToSlug,
} from '../_ui/_blocks/Form/Inputs/SelectPersonalTitle/SelectPersonalTitle';
import Attention from '../_ui/_blocks/Attention/Attention';
import Card from '../_ui/_blocks/Card/Card';
import Button from '../_ui/_blocks/Buttons/Button/Button';
import Typography from '../_ui/_blocks/Typography/Typography';
import TextInput from '../_ui/_blocks/Form/Inputs/TextInput/TextInput';

import styles from './UpdateDeliveryAddress.module.css';
import Modal from '../_ui/Modal/Modal';
import Icon from '../_ui/_blocks/Icon/Icon';

export const dataTypeToUpdate = {
    ORDER: 'ORDER',
    PRESCRIPTION: 'PRESCRIPTION',
};

interface Props {
    uuidOfItemToUpdate: string | number;
    currentAddress: Record<string, any> | string | null;
    isOpen: boolean;
    onClose: () => void;
    onChange: (e: any) => void;
    updateType?: string;
}

const UpdateDeliveryAddress = ({
    uuidOfItemToUpdate,
    currentAddress = null,
    isOpen = false,
    onClose,
    onChange,
    updateType = dataTypeToUpdate.ORDER,
}: Props) => {
    const [isNew, setIsNew] = useState(false);
    const [formState, setFormState] = useState<{
        title?: string;
        firstname?: string;
        middlename?: string;
        lastname?: string;
        post_through_letterbox?: boolean;
        uuid?: string;
        address?: {
            uuid: string;
            [key: string]: any;
        };
    }>({});

    /**
     * Get users delivery addresses.
     */
    const [{ isLoading: loadingAddresses, data }, getAddressList] = useDataApiEffect(addressesService.getAllAddresses);

    /**
     * Send update order delivery address.
     */
    const handleAddressChange = useCallback(
        (address: any) => {
            if (updateType === dataTypeToUpdate.ORDER) {
                return ordersService.updateOrderDeliveryAddress(uuidOfItemToUpdate.toString(), address);
            }

            const formattedAddress = {
                ...address,
                salutation: address.title,
            };
            return prescriptionRequestService.updatePrescriptionRequestDeliveryAddress(uuidOfItemToUpdate.toString(), formattedAddress);
        },
        [uuidOfItemToUpdate, updateType]
    );

    const [{ isLoading: isSubmitting, error }, submitAddressChange] = useDataApi(handleAddressChange, [handleAddressChange]);

    /**
     * Save a new address
     */
    // @ts-expect-error
    const [{ createAddressError }, createAddress] = useDataApi(
        // @ts-expect-error
        (addressClone) => addressesService.createAddress(addressClone),
        [uuidOfItemToUpdate]
    );

    // @ts-expect-error
    const updateAddressFormState = (address) => setFormState((state) => ({ ...state, ...address }));
    // @ts-expect-error
    const handleSelectAddress = (address) => {
        setFormState(address);
    };
    // @ts-expect-error
    const updateFormState = (name, value) => setFormState((state) => ({ ...state, [name]: value }));

    useEffect(() => {
        // check data exists
        if (data && data.data && currentAddress && isOpen === true) {
            // @ts-expect-error
            const foundAddress = data.data.find((address) => address.uuid === currentAddress.uuid);

            if (foundAddress) {
                handleSelectAddress(foundAddress);
            }
        }
    }, [data, currentAddress, isOpen]);

    /**
     * When an address is selected from the list of current saved addresses.
     * @returns {void}
     */
    const handleAddressSelected = () => {
        setFormState({
            title: formState.title,
            firstname: formState.firstname,
            middlename: formState.middlename,
            lastname: formState.lastname,
            post_through_letterbox: false,
        });
        setIsNew(true);
    };

    /**
     * Adds a new address to the item.
     * @returns {void}
     */
    const handleAddNewAddress = async () => {
        const addressClone = { ...formState };
        let newAddress = null;

        // If address is new, try to save it to the users address book
        // Cast it to a var so we can get the uuid of the newly created addess for use on the item
        if (isNew) {
            newAddress = await createAddress({ ...addressClone, default: false });
        }

        addressClone.address!.uuid = newAddress ? newAddress.data.data.uuid : addressClone.uuid;
        delete addressClone.uuid;

        const response = await submitAddressChange(addressClone);
        if (!response) return;

        onChange(response.data.data);
        getAddressList();
        onClose();
    };

    const userAddresses = get(data, 'data', []);

    return (
        <Modal show={isOpen} onClose={onClose} className="container-xsmall w-full">
            <Card as="div" className={`${styles.dialog} ${styles.dialogSmall} space-y-200 mx-auto`} disableBoxShadow>
                <div className="flex w-full justify-end">
                    <Button title="close" type="button" variant="none" className="p-0" onClick={onClose}>
                        <Icon size="small" icon='cross-light' alt="close Icon" />
                    </Button>
                </div>
                <div>
                    <Modal.Title typeset="title" className="text-center">
                        Update Address
                    </Modal.Title>
                    <Modal.Description as="p" color="quiet" className="text-center">
                        Update your Delivery Address
                    </Modal.Description>
                </div>
                <Loading isLoading={loadingAddresses || !formState}>
                    <JoiForm
                        schema={validationSchema}
                        formState={formState}
                        onSubmit={handleAddNewAddress}
                        className="space-y-150"
                        options={{ allowUnknown: true }}
                    >
                        {isNew || !userAddresses.length ? (
                            <Fragment>
                                <fieldset className="space-y-150">
                                    <JoiFormInputWrap validateOnChange name="title">
                                        <SelectPersonalTitle
                                            label="Title"
                                            title={titleReadableToSlug(formState.title!)}
                                            onChange={(e) => {
                                                updateFormState('title', titleSlugToReadable(e.target.value));
                                            }}
                                        />
                                    </JoiFormInputWrap>
                                    <JoiFormInputWrap name="firstname" validateOnChange>
                                        <TextInput
                                            label="First name"
                                            className="w-full"
                                            id="form-field--firstname"
                                            name="firstname"
                                            value={formState.firstname}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                updateFormState('firstname', e.target.value)
                                            }
                                        />
                                    </JoiFormInputWrap>
                                    <JoiFormInputWrap name="middlename" validateOnChange>
                                        <TextInput
                                            label="Middle name"
                                            optional
                                            className="w-full"
                                            id="form-field--middlename"
                                            name="middlename"
                                            value={formState.middlename}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                updateFormState('middleName', e.target.value)
                                            }
                                        />
                                    </JoiFormInputWrap>
                                    <JoiFormInputWrap name="lastname" validateOnChange>
                                        <TextInput
                                            label="Last name"
                                            className="w-full"
                                            id="form-field--lastname"
                                            name="lastname"
                                            value={formState.lastname}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                updateFormState('lastname', e.target.value)
                                            }
                                        />
                                    </JoiFormInputWrap>
                                </fieldset>
                                <AddressForm deliveryAddress name="address" defaultValue={formState} onChange={updateAddressFormState} />
                            </Fragment>
                        ) : (
                            <JoiFormInputWrap name="address" validateOnChange>
                                <AddressPicker
                                    userAddresses={userAddresses}
                                    // @ts-expect-error
                                    selectedAddress={formState}
                                    onSelect={handleSelectAddress}
                                    onNew={handleAddressSelected}
                                />
                            </JoiFormInputWrap>
                        )}
                        {error || createAddressError ? (
                            <Attention type="error" heading="Update error">
                                <p className="text-content-quiet">{API_ERROR_MESSAGE}</p>
                            </Attention>
                        ) : null}
                        <div className="flex flex-col justify-center">
                            <ButtonBusy title="Change address" type="submit" size="large" loading={isSubmitting}>
                                Change address
                            </ButtonBusy>
                            <Button variant="none" title="cancel" type="button" className="w-fit mx-auto" onClick={onClose}>
                                <Typography typeset="link">Cancel</Typography>
                            </Button>
                        </div>
                    </JoiForm>
                </Loading>
            </Card>
        </Modal>
    );
};

export default UpdateDeliveryAddress;
